import { html, css } from 'lit';

import '../../../../packages/neb-lit-components/src/components/inputs/neb-select';
import '../../../../packages/neb-lit-components/src/components/neb-radio-button';
import '../../../../packages/neb-lit-components/src/components/controls/neb-switch';

import NebForm, {
  ELEMENTS as ELEMENTS_BASE,
} from '../../../../packages/neb-lit-components/src/components/forms/neb-form';
import { BUTTON_ROLE } from '../../../../packages/neb-lit-components/src/components/neb-button';
import { printPdf } from '../../../../packages/neb-utils/neb-pdf-print-util';
import { select } from '../../../../packages/neb-utils/selectors';
import { printCheckIn } from '../../../api-clients/print-check-in';
import {
  CSS_FONT_WEIGHT_BOLD,
  CSS_SPACING,
  CSS_COLOR_WHITE,
  CSS_COLOR_HIGHLIGHT,
  CSS_SPACING_ROW,
} from '../../../styles';
import { genDefaultStyle } from '../../misc/neb-qrcode';

export const ELEMENTS = {
  ...ELEMENTS_BASE,
  switchAutoCheckIn: { id: 'auto-check-in' },
  switchEnableSelfCheckIn: { id: 'enable-self-check-in' },
  link: { id: 'link' },
  copyCheckInLinkButton: { id: 'button-copy-link' },
  openCheckInLinkButton: { id: 'button-open-link' },
  copyInput: { id: 'input-copy' },
  copyHtmlCodeButton: { id: 'button-copy-html' },
  printQRCodeButton: { id: 'button-qr-code' },
  qrCode: { id: 'qr-code' },
  selfCheckInIntervalSelect: { id: 'select-self-check-in-interval' },
  selfCheckInRedirectDelaySelect: { id: 'select-self-check-in-redirect-delay' },
  methodRadioButtons: { selector: '.method-radio-button' },
};

export const SELF_CHECK_IN_METHODS = {
  NAME_AND_DOB_ONLY: 'Name and DOB Only',
  PIN_ONLY: 'PIN Only',
  BOTH: 'Both',
};

export const SELF_CHECK_IN_INTERVAL_ITEMS = [
  { label: 'Same Day', data: { id: 288 } },
  { label: '30 Minutes', data: { id: 6 } },
  { label: '1 Hour', data: { id: 12 } },
  { label: '2 Hours', data: { id: 24 } },
  { label: '3 Hours', data: { id: 36 } },
  { label: '4 Hours', data: { id: 48 } },
  { label: '5 Hours', data: { id: 60 } },
  { label: '6 Hours', data: { id: 72 } },
];
export const SELF_CHECK_IN_REDIRECT_DELAY_ITEMS = [
  { label: 'Never', data: { id: '' } },
  { label: 'Instantly', data: { id: 0 } },
  { label: '5 seconds', data: { id: 5 } },
  { label: '10 seconds', data: { id: 10 } },
  { label: '30 seconds', data: { id: 30 } },
  { label: '1 Minute', data: { id: 60 } },
  { label: '5 Minutes', data: { id: 300 } },
];

export default class NebFormSelfCheckIn extends NebForm {
  static get properties() {
    return {
      __rawQrSvg: String,
      __qrConfig: Object,
      layout: {
        reflect: true,
        type: String,
      },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .layout {
          display: flex;
          margin: 0px ${CSS_SPACING} ${CSS_SPACING};
        }

        .text {
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .column {
          padding: ${CSS_SPACING};
          border-radius: 4px;
          background-color: ${CSS_COLOR_WHITE};
        }

        .row {
          padding: ${CSS_SPACING} 0 0 0;
        }

        .row-button {
          padding: ${CSS_SPACING} 0px ${CSS_SPACING} 0;
        }

        .button {
          margin-bottom: ${CSS_SPACING_ROW};
        }

        .switch {
          width: fit-content;
        }

        .label {
          padding: ${CSS_SPACING};
        }

        .toggleCheckIn {
          margin-top: -5px;
          align-items: center;
          display: flex;
        }

        .select {
          width: 250px;
        }

        .method-radio-buttons-container {
          padding-top: 10px;
          margin-left: -10px;
        }

        .method-section {
          padding-top: 20px;
        }
      `,
    ];
  }

  static createModel() {
    return {
      checkInUrl: `${window.location.origin}/portal/`,
      autoCheckIn: false,
      selfCheckInInterval: 12,
      selfCheckInRedirectDelay: '',
      selfCheckInMethod: SELF_CHECK_IN_METHODS.NAME_AND_DOB_ONLY,
      enableSelfCheckIn: true,
    };
  }

  initState() {
    super.initState();

    this.__rawQrSvg = '';
    this.__qrConfig = genDefaultStyle();
  }

  initHandlers() {
    super.initHandlers();

    this.handlers = {
      ...this.handlers,

      copyCheckInSiteLink: () => {
        this.__copyInputEl.style.display = '';
        this.__copyInputEl.value = this.model.checkInUrl;

        this.__copyInputEl.select();

        document.execCommand('copy');
        this.__copyInputEl.style.display = 'none';
      },
      openCheckInSiteLink: () => {
        window.open(this.model.checkInUrl, '_blank');
      },
      copyHtmlCode: () => {
        this.__copyInputEl.style.display = '';
        this.__copyInputEl.value = `
          <input
            type="button"
            value="CHECK IN"
            autocomplete="off"
            style="
              outline: none;
              width: 185px;
              height: 40px;
              border-radius: 20px;
              background-color: ${CSS_COLOR_HIGHLIGHT};
              font-size: 14px;
              font-weight: 700;
              color: ${CSS_COLOR_WHITE};
            "
            onClick="location.href='${this.model.checkInUrl}'"
          />
        `;

        this.__copyInputEl.select();

        document.execCommand('copy');
        this.__copyInputEl.style.display = 'none';
      },
      getSvg: async svg => {
        this.__rawQrSvg = await svg.text();
      },
      printQr: async () => {
        try {
          if (this.__rawQrSvg) {
            await printPdf(
              printCheckIn(this.__rawQrSvg, this.model.checkInUrl),
            );
          }
        } catch (e) {
          console.error(e);
        }
      },
    };
  }

  firstUpdated() {
    super.firstUpdated();

    this.__copyInputEl = this.shadowRoot.getElementById(ELEMENTS.copyInput.id);
  }

  createSelectors() {
    return {
      children: {
        selfCheckInInterval: {
          ...select(
            SELF_CHECK_IN_INTERVAL_ITEMS,
            SELF_CHECK_IN_INTERVAL_ITEMS[2],
          ),
        },
        selfCheckInRedirectDelay: {
          ...select(
            SELF_CHECK_IN_REDIRECT_DELAY_ITEMS,
            SELF_CHECK_IN_REDIRECT_DELAY_ITEMS[0],
          ),
        },
      },
    };
  }

  renderCheckInInterval() {
    return html`
      <p class="row-button">
        How long before the appointment can the patients check in on the day of
        the appointment?
      </p>
      <neb-select
        id="${ELEMENTS.selfCheckInIntervalSelect.id}"
        class="select"
        name="selfCheckInInterval"
        .items="${SELF_CHECK_IN_INTERVAL_ITEMS}"
        .value="${this.state.selfCheckInInterval}"
        .onChange="${this.handlers.change}"
      ></neb-select>
    `;
  }

  renderCheckInRedirectDelay() {
    return html`
      <p class="row-button">
        How quickly should the page return to the homepage after check in?
      </p>
      <neb-select
        id="${ELEMENTS.selfCheckInRedirectDelaySelect.id}"
        class="select"
        name="selfCheckInRedirectDelay"
        .items="${SELF_CHECK_IN_REDIRECT_DELAY_ITEMS}"
        .value="${this.state.selfCheckInRedirectDelay}"
        .onChange="${this.handlers.change}"
      ></neb-select>
    `;
  }

  renderContent() {
    return html`
      <div class="column">
        <div class="toggleCheckIn">
          <neb-switch
            id="${ELEMENTS.switchEnableSelfCheckIn.id}"
            class="switch"
            name="enableSelfCheckIn"
            .onChange="${this.handlers.change}"
            ?on="${this.state.enableSelfCheckIn}"
          ></neb-switch>

          <p class="text">Allow patients to self check in online</p>
        </div>

        <div class="toggleCheckIn method-section">
          <neb-switch
            id="${ELEMENTS.switchAutoCheckIn.id}"
            class="switch"
            name="autoCheckIn"
            .onChange="${this.handlers.change}"
            ?on="${this.state.autoCheckIn}"
          ></neb-switch>
          <p class="text">
            If a patient checks in online, automatically check the appointment
            in on the schedule
          </p>
        </div>

        <div class="method-section">
          <div>
            <p class="text">Self Check In Method</p>

            <p>Which values should patients use to self check in?</p>
          </div>

          <div class="method-radio-buttons-container">
            <neb-radio-button
              label="First Name, Last Name & Date of Birth only"
              name="selfCheckInMethod"
              class="method-radio-button"
              .checked="${this.state.selfCheckInMethod ===
              SELF_CHECK_IN_METHODS.NAME_AND_DOB_ONLY}"
              .value="${SELF_CHECK_IN_METHODS.NAME_AND_DOB_ONLY}"
              .onChange="${this.handlers.change}"
            ></neb-radio-button>

            <neb-radio-button
              label="Self Check In PIN only"
              name="selfCheckInMethod"
              class="method-radio-button"
              .checked="${this.state.selfCheckInMethod ===
              SELF_CHECK_IN_METHODS.PIN_ONLY}"
              .value="${SELF_CHECK_IN_METHODS.PIN_ONLY}"
              .onChange="${this.handlers.change}"
            ></neb-radio-button>

            <neb-radio-button
              label="Allow Both"
              name="selfCheckInMethod"
              class="method-radio-button"
              .checked="${this.state.selfCheckInMethod ===
              SELF_CHECK_IN_METHODS.BOTH}"
              .value="${SELF_CHECK_IN_METHODS.BOTH}"
              .onChange="${this.handlers.change}"
            ></neb-radio-button>
          </div>
        </div>

        <p class="text row">Self Check In Policies</p>
        ${this.renderCheckInInterval()} ${this.renderCheckInRedirectDelay()}
      </div>

      <div class="column">
        <p class="text">
          Put a direct link in an email or text message for your patients to
          check in
        </p>

        <div class="row">
          <a
            id="${ELEMENTS.link.id}"
            href="${this.model.checkInUrl}"
            target="_blank"
          >
            ${this.model.checkInUrl}
          </a>
        </div>

        <div class="row row-button">
          <neb-button
            class="button"
            id="${ELEMENTS.copyCheckInLinkButton.id}"
            label="COPY CHECK IN SITE LINK"
            .role="${BUTTON_ROLE.CONFIRM}"
            @click="${this.handlers.copyCheckInSiteLink}"
          ></neb-button>

          <neb-button
            id="${ELEMENTS.openCheckInLinkButton.id}"
            label="OPEN CHECK IN SITE"
            .role="${BUTTON_ROLE.OUTLINE}"
            @click="${this.handlers.openCheckInSiteLink}"
          ></neb-button>
        </div>

        <p class="text">
          Add the ability for a patient to check in via your website
        </p>

        <div class="row row-button">
          <neb-button
            id="${ELEMENTS.copyHtmlCodeButton.id}"
            label="COPY HTML CODE"
            .role="${BUTTON_ROLE.CONFIRM}"
            @click="${this.handlers.copyHtmlCode}"
          ></neb-button>
        </div>

        <p class="text">Print a QR Code to allow your patients to check in</p>

        <div class="row">
          <neb-qrcode
            id="${ELEMENTS.qrCode.id}"
            .data="${this.model.checkInUrl}"
            .qrConfig="${this.__qrConfig}"
            .onRenderQr="${this.handlers.getSvg}"
          ></neb-qrcode>
        </div>

        <div class="row row-button">
          <neb-button
            id="${ELEMENTS.printQRCodeButton.id}"
            label="Print QR Code"
            .role="${BUTTON_ROLE.CONFIRM}"
            .onClick="${this.handlers.printQr}"
          ></neb-button>
        </div>
      </div>

      <input
        autocomplete="off"
        id="${ELEMENTS.copyInput.id}"
        style="display: none;"
      />
    `;
  }
}

customElements.define('neb-form-self-check-in', NebFormSelfCheckIn);
